import { z } from 'zod';

/**
 * SIL-1595: ops Escrow.com console DTOs (spec 4.6 / 9).
 *
 * The reason bounds mirror the escrow_ops_action CHECK (migration 1041): 10-1000
 * characters after trim, no control characters, no `token=`. The DB is the
 * authority; this schema only fails fast with a friendly 400.
 */
export const ESCROW_OPS_REASON_MIN = 10;
export const ESCROW_OPS_REASON_MAX = 1000;

export const escrowOpsReasonSchema = z
  .string()
  .transform((s) => s.trim())
  .pipe(
    z
      .string()
      .min(ESCROW_OPS_REASON_MIN, `Reason must be at least ${ESCROW_OPS_REASON_MIN} characters`)
      .max(ESCROW_OPS_REASON_MAX)
      // eslint-disable-next-line no-control-regex
      .refine((s) => !/[\u0000-\u001f\u007f]/.test(s), 'Reason must be plain text')
      .refine((s) => !s.toLowerCase().includes('token='), 'Reason must not contain a token'),
  );

export const escrowOpsActionBodySchema = z.object({ reason: escrowOpsReasonSchema });
export type EscrowOpsActionBody = z.infer<typeof escrowOpsActionBodySchema>;

/** SIL-1599: "Retry completion" - the reason only; unknown keys are rejected (nothing else can steer it). */
export const escrowOpsRetryCompletionBodySchema = z.object({ reason: escrowOpsReasonSchema }).strict();
export type EscrowOpsRetryCompletionBody = z.infer<typeof escrowOpsRetryCompletionBodySchema>;

/** SIL-1600: "Cancel now" - the reason only; unknown keys are rejected. */
export const escrowOpsCancelNowBodySchema = z.object({ reason: escrowOpsReasonSchema }).strict();
export type EscrowOpsCancelNowBody = z.infer<typeof escrowOpsCancelNowBodySchema>;

/**
 * SIL-1600: "Extend deadline" - an ISO-8601 instant with an offset plus the reason. Unknown keys are rejected.
 * The DB is the only bounds authority (count cap, horizon from the original deadline, not earlier than now + 1
 * minute); the UI picker bounds are UX only.
 */
export const escrowOpsExtendDeadlineBodySchema = z
  .object({ deadlineAt: z.string().datetime({ offset: true }), reason: escrowOpsReasonSchema })
  .strict();
export type EscrowOpsExtendDeadlineBody = z.infer<typeof escrowOpsExtendDeadlineBodySchema>;

/** SIL-1597: clear-flag body. `flag` is validated here and bound as a parameter, never interpolated. */
export const escrowOpsClearFlagBodySchema = z.object({
  flag: z.enum(['adverse', 'dispute']),
  reason: escrowOpsReasonSchema,
});
export type EscrowOpsClearFlagBody = z.infer<typeof escrowOpsClearFlagBodySchema>;

/**
 * SIL-1742: confirm that Escrow.com paid a FULL refund of a deposit. Every field is validated again by
 * escrow_ops_confirm_refund (the DB is the authority); this schema only fails fast. None of the evidence fields
 * is prefilled by the portal: the Escrow.com transaction id and the amount must be TYPED from the vendor page.
 * The reference admits no URL, email or token characters (no `:` `/` `@`).
 */
export const ESCROW_REFUND_REFERENCE_RE = /^[A-Za-z0-9][A-Za-z0-9 ._#-]{2,79}$/;
const escrowRefundReferenceSchema = z
  .string()
  .transform((s) => s.trim())
  .pipe(
    z
      .string()
      .regex(ESCROW_REFUND_REFERENCE_RE, 'Reference must be 3-80 letters, digits, spaces or . _ # -')
      .refine((s) => !s.toLowerCase().includes('www.') && !s.toLowerCase().includes('token='), 'Reference must not be a link or token'),
  );
/** SIL-1742 S5: no Unicode bidi / zero-width characters (they can disguise audit text). Mirrors the DB check. */
export const ESCROW_HIDDEN_CHARS_RE = /[\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/;
const escrowRefundReasonSchema = escrowOpsReasonSchema
  .refine((s) => !s.includes('@'), 'Reason must not contain an email address')
  .refine((s) => !ESCROW_HIDDEN_CHARS_RE.test(s), 'Reason must not contain hidden or direction-control characters');
/** A real calendar date (YYYY-MM-DD): rejects 2026-02-31 and 2026-13-45 before the DB cast can fail. */
const escrowIsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD (UTC)')
  .refine((s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
  }, 'Date is not a real calendar date');
export const escrowOpsConfirmRefundBodySchema = z
  .object({
    reason: escrowRefundReasonSchema,
    providerTransactionId: z.string().regex(/^\d{1,15}$/, 'Escrow.com transaction id must be digits'),
    vendorReference: escrowRefundReferenceSchema,
    refundAmountUsd: z.string().regex(/^\d{1,10}\.\d{2}$/, 'Amount must be dollars and cents, e.g. 2000.00'),
    refundedOn: escrowIsoDateSchema,
  })
  .strict();
export type EscrowOpsConfirmRefundBody = z.infer<typeof escrowOpsConfirmRefundBodySchema>;

/** SIL-1742: void a confirmation (a DIFFERENT ops admin, inside the void window). */
export const escrowOpsVoidRefundBodySchema = z
  .object({
    reason: escrowRefundReasonSchema,
    evidenceReference: escrowRefundReferenceSchema,
  })
  .strict();
export type EscrowOpsVoidRefundBody = z.infer<typeof escrowOpsVoidRefundBodySchema>;

/**
 * SIL-1742 C1: close out a deposit that was funded per the Escrow.com flags but has NO offer_payment row (never touches
 * offer_payment). The snapshot is identical while Escrow.com still holds the wire and after it was returned, so this is an
 * ATTESTATION: a constrained outcome, a reference to what the admin checked, and a date.
 */
export const ESCROW_CLOSEOUT_OUTCOMES = ['returned_to_buyer', 'never_arrived'] as const;
export const ESCROW_CLOSEOUT_OUTCOME_LABELS: Record<(typeof ESCROW_CLOSEOUT_OUTCOMES)[number], string> = {
  returned_to_buyer: 'The funds were returned to the buyer',
  never_arrived: 'The funds never arrived at Escrow.com',
};
export const escrowOpsCloseUnrecordedRefundBodySchema = z
  .object({
    reason: escrowRefundReasonSchema,
    providerTransactionId: z.string().regex(/^\d{1,15}$/, 'Escrow.com transaction id must be digits'),
    outcome: z.enum(ESCROW_CLOSEOUT_OUTCOMES),
    evidenceReference: escrowRefundReferenceSchema,
    outcomeOn: escrowIsoDateSchema,
  })
  .strict();
export type EscrowOpsCloseUnrecordedRefundBody = z.infer<typeof escrowOpsCloseUnrecordedRefundBodySchema>;

/** SIL-1742: reverse a close-out (a DIFFERENT ops admin, inside the void window). */
export const escrowOpsVoidCloseUnrecordedRefundBodySchema = escrowOpsVoidRefundBodySchema;
export type EscrowOpsVoidCloseUnrecordedRefundBody = z.infer<typeof escrowOpsVoidCloseUnrecordedRefundBodySchema>;

/**
 * SIL-1840: two-person override to confirm a refund when Escrow.com returns `items: []` (migration 1087). The request carries
 * the Escrow.com refund STATEMENT as a base64 file; the API hashes it itself (a client-supplied hash is never accepted) and
 * the approver supplies their own copy, which must hash to the same value. None of the evidence fields is prefilled.
 * SIL-1920: the user's file name is NOT part of the contract (never stored; the object key is refund-evidence/<txn>/<sha>.<ext>,
 * the type is sniffed from the bytes); base64 must be canonical (the API re-encodes and compares).
 */
export const ESCROW_OVERRIDE_STATEMENT_MAX_BYTES = 3 * 1024 * 1024;
const escrowStatementBase64Schema = z
  .string()
  .min(8, 'Attach the Escrow.com refund statement')
  .max(Math.ceil((ESCROW_OVERRIDE_STATEMENT_MAX_BYTES * 4) / 3) + 8, 'The statement is larger than 3 MB')
  .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'The statement must be a file');
export const escrowOpsRequestRefundOverrideBodySchema = z
  .object({
    reason: escrowRefundReasonSchema,
    providerTransactionId: z.string().regex(/^\d{1,15}$/, 'Escrow.com transaction id must be digits'),
    vendorReference: escrowRefundReferenceSchema,
    refundAmountUsd: z.string().regex(/^\d{1,10}\.\d{2}$/, 'Amount must be dollars and cents, e.g. 2000.00'),
    refundedOn: escrowIsoDateSchema,
    statementBase64: escrowStatementBase64Schema,
  })
  .strict();
export type EscrowOpsRequestRefundOverrideBody = z.infer<typeof escrowOpsRequestRefundOverrideBodySchema>;
export const escrowOpsApproveRefundOverrideBodySchema = z
  .object({
    reason: escrowRefundReasonSchema,
    providerTransactionId: z.string().regex(/^\d{1,15}$/, 'Escrow.com transaction id must be digits'),
    statementBase64: escrowStatementBase64Schema,
  })
  .strict();
export type EscrowOpsApproveRefundOverrideBody = z.infer<typeof escrowOpsApproveRefundOverrideBodySchema>;
/** finalize / cancel: a reason only. */
export const escrowOpsRefundOverrideReasonBodySchema = z.object({ reason: escrowRefundReasonSchema }).strict();
export type EscrowOpsRefundOverrideReasonBody = z.infer<typeof escrowOpsRefundOverrideReasonBodySchema>;

/**
 * SIL-1726: two-person refresh of the party emails of a FAILED create (migration 1113). The body is the reason only (unknown
 * keys rejected): no email, id or flag can steer it, the DB re-reads the org contact emails itself. The reason must not carry an
 * address (the DB rejects `@`; this fails fast with a friendly 400).
 */
export const escrowOpsEmailRefreshReasonSchema = escrowOpsReasonSchema.refine(
  (s) => !s.includes('@'),
  'Do not paste email addresses into the reason',
);
export const escrowOpsEmailRefreshBodySchema = z.object({ reason: escrowOpsEmailRefreshReasonSchema }).strict();
export type EscrowOpsEmailRefreshBody = z.infer<typeof escrowOpsEmailRefreshBodySchema>;

/** One party of a refresh preview: fingerprints and masked values only (local part masked, domain visible); never an address. */
export const escrowEmailRefreshPartySchema = z.object({
  old_fp: z.string().nullable(),
  new_fp: z.string(),
  old_mask: z.string().nullable(),
  new_mask: z.string(),
});
export type EscrowEmailRefreshParty = z.infer<typeof escrowEmailRefreshPartySchema>;
export const escrowEmailRefreshPartiesSchema = z.object({
  buyer: escrowEmailRefreshPartySchema.optional(),
  seller: escrowEmailRefreshPartySchema.optional(),
});
export type EscrowEmailRefreshParties = z.infer<typeof escrowEmailRefreshPartiesSchema>;

export const escrowEmailRefreshPreviewSchema = z.object({
  eligible: z.boolean(),
  /** Fixed code explaining why not (see ESCROW_OPS_DETAIL_LABELS), or null. */
  blocker: z.string().nullable(),
  parties: escrowEmailRefreshPartiesSchema,
  openRequest: z
    .object({
      requestId: z.string().uuid(),
      requestedBy: z.string(),
      createdAt: z.string(),
      expiresAt: z.string(),
      parties: escrowEmailRefreshPartiesSchema,
      /** Server decision (the DB re-checks): the caller is not the requester. */
      canApprove: z.boolean().optional(),
    })
    .nullable(),
});
export type EscrowEmailRefreshPreview = z.infer<typeof escrowEmailRefreshPreviewSchema>;

/** The result of request / approve / cancel: the action result plus the request id and the masked parties. */
export const escrowEmailRefreshResultSchema = z.object({
  actionId: z.number(),
  result: z.enum(['queued', 'skipped', 'applied']),
  detail: z.string(),
  requestId: z.string().uuid().nullable().optional(),
  parties: escrowEmailRefreshPartiesSchema.optional(),
});
export type EscrowEmailRefreshResult = z.infer<typeof escrowEmailRefreshResultSchema>;

export const escrowOpsActionKindSchema = z.enum([
  'refetch',
  'retry_create',
  'clear_flag',
  'start_refund_path',
  'partner_cancel',
  // SIL-1598
  'request_balance',
  'recreate_balance',
  // SIL-1599
  'retry_completion',
  // SIL-1600
  'extend_deadline',
  'cancel_now',
  // SIL-1742
  'confirm_refund',
  'void_refund_confirm',
  'close_unrecorded_refund',
  'void_close_unrecorded_refund',
  // SIL-1840
  'request_refund_override',
  'approve_refund_override',
  'cancel_refund_override',
  'confirm_refund_override',
  // SIL-1726
  'request_email_refresh',
  'approve_email_refresh',
  'cancel_email_refresh',
]);
export type EscrowOpsActionKind = z.infer<typeof escrowOpsActionKindSchema>;

export const escrowOpsActionResultSchema = z.object({
  actionId: z.number(),
  result: z.enum(['queued', 'skipped', 'applied']),
  /** Fixed code (queued, leased, backoff, cooldown, daily_cap, ...), never provider text. */
  detail: z.string(),
});
export type EscrowOpsActionResult = z.infer<typeof escrowOpsActionResultSchema>;

/** SIL-1600: the extend-deadline result adds the resulting deadline and how many extensions are used (of 3). */
export const escrowOpsExtendResultSchema = escrowOpsActionResultSchema.extend({
  offerId: z.string().uuid(),
  deadlineAt: z.string().nullable(),
  extensionsUsed: z.number(),
});
export type EscrowOpsExtendResult = z.infer<typeof escrowOpsExtendResultSchema>;
/** SIL-1600: applied extensions allowed per offer + kind (mirrors escrow_ops_extend_deadline). */
export const ESCROW_MAX_DEADLINE_EXTENSIONS = 3;

/** Human text for a result_detail code. Unknown codes fall back to the code itself. */
export const ESCROW_OPS_DETAIL_LABELS: Record<string, string> = {
  queued: 'Queued - the processor applies it on its next sweep',
  leased: 'Skipped - an Escrow.com call is already in flight',
  backoff: 'Skipped - the row is backing off after fetch failures',
  cooldown: 'Skipped - too soon after the previous request',
  daily_cap: 'Skipped - daily retry cap reached',
  already_queued: 'Skipped - a re-fetch is already queued',
  not_open: 'Skipped - transaction is not open',
  not_pending_create: 'Skipped - transaction is not waiting to be created',
  not_exhausted: 'Skipped - create has not exhausted its attempts yet',
  no_error: 'Skipped - no failed create to retry',
  no_provider_id: 'Skipped - no Escrow.com transaction id',
  kind_not_swept: 'Skipped - this transaction kind is not swept',
  cleared_adverse: 'Applied - adverse flag cleared (confirmed once a later fetch agrees)',
  cleared_dispute: 'Applied - dispute flag cleared (confirmed once a later fetch agrees)',
  refund_path_started: 'Applied - offer cancelled, refund path started',
  no_such_flag: 'Skipped - that flag is not set',
  snapshot_stale: 'Skipped - snapshot is older than 2 minutes; re-fetch first',
  provider_still_adverse: 'Skipped - Escrow.com still shows the transaction as adverse',
  provider_still_in_dispute: 'Skipped - Escrow.com still shows a dispute',
  no_flag: 'Skipped - the transaction has no adverse or dispute flag',
  already_started: 'Skipped - the refund path is already started',
  offer_terminal: 'Skipped - the offer is already in a terminal state',
  offer_completed: 'Skipped - the offer is already completed',
  not_eligible: 'Skipped - a deposit needs a flag, an overdue deadline or a bind alert; a balance needs to be live on a cancelled offer on the refund path (a superseded balance is never cancelled from here)',
  already_canceled: 'Skipped - already canceled at Escrow.com',
  funded_vendor_refuses: 'Skipped - SiliconX never cancels a funded Escrow.com transaction. The buyer or seller opens the cancellation or dispute at Escrow.com; once they have, re-fetch here and record the outcome',
  in_dispute: 'Skipped - the transaction is in dispute',
  attempts_exhausted: 'Skipped - the previous cancel request ran out of attempts; request it again',
  // SIL-1598: request / recreate the balance
  requested: 'Queued - the balance transaction will be created by the processor',
  recreated: 'Queued - the old balance is superseded and a replacement will be created at the current price',
  already_requested: 'Skipped - a balance is already requested at the current price',
  balance_not_positive: 'Skipped - the agreed price leaves no balance after the deposit',
  flagged: 'Skipped - the offer has an open escrow review (flag, dispute or refund path)',
  no_balance: 'Skipped - there is no live balance to recreate',
  funded_not_recreatable: 'Skipped - the balance is funded or in flight and cannot be recreated',
  cancel_outstanding: 'Skipped - the previous balance transaction is still being cancelled',
  // SIL-1599: retry completion
  offer_not_deposit_held: 'Skipped - the offer is not waiting for completion',
  already_completed: 'Skipped - the offer is already completed',
  no_live_txns: 'Skipped - the offer needs exactly one live deposit and one live balance',
  not_accepted: 'Skipped - the buyer has not accepted the balance on Escrow.com yet',
  // SIL-1600: extend deadline / cancel now
  extended: 'Applied - the deadline was extended',
  deposit_expiry_queued: 'Queued - the deposit window ends early; the processor expires it on its next sweep (the contract deadline is unchanged)',
  balance_cancel_queued: 'Queued - the processor cancels the unpaid balance at Escrow.com (the contract deadline is unchanged)',
  offer_not_active: 'Skipped - the offer is no longer in the state this transaction belongs to',
  // SIL-1742: confirm / void a refund
  refund_confirmed: 'Applied - refund recorded; the payment is now refunded',
  refund_confirmation_voided: 'Applied - the confirmation was voided; the payment is held again',
  already_confirmed: 'Skipped - this refund is already confirmed',
  not_deposit: 'Skipped - only a live deposit or balance can be refunded here',
  provider_id_mismatch: 'Skipped - the Escrow.com transaction id does not match this deal',
  payment_missing: 'Skipped - there is no payment on record for this transaction (use Close out)',
  payment_not_escrow_com: 'Skipped - the payment is not an Escrow.com payment',
  payment_not_held: 'Skipped - the payment is not held (released or already refunded)',
  offer_not_terminal: 'Skipped - the offer has not been cancelled, expired or refused',
  too_early: 'Skipped - too soon after the refund path started (for a balance, or after the Escrow.com cancel); wait, then retry',
  bind_mismatch_open: 'Skipped - Escrow.com returned an unreadable snapshot; contact engineering',
  no_snapshot: 'Skipped - no verified snapshot on record; re-fetch first',
  stale_snapshot: 'Skipped - the snapshot is too old; re-fetch, then retry',
  unknown_shape: 'Skipped - the Escrow.com snapshot is not in a confirmable shape; contact engineering',
  not_canceled: 'Skipped - Escrow.com does not show the transaction as canceled',
  still_secured: 'Skipped - Escrow.com still shows the funds as secured',
  accepted: 'Skipped - the buyer accepted on Escrow.com; the seller is owed',
  disbursed: 'Skipped - the funds were disbursed to the seller',
  never_funded: 'Skipped - no money ever arrived, so no refund is owed',
  amount_mismatch: 'Skipped - the amount must equal the transaction amount exactly (full refunds only)',
  refund_date_before_funding: 'Skipped - the refund date is before the transaction was funded',
  reference_reused: 'Skipped - that refund reference is already used on another confirmation',
  no_active_confirmation: 'Skipped - there is no active refund confirmation to void',
  same_actor: 'Skipped - a second ops admin must void this confirmation',
  window_elapsed: 'Skipped - the void window has passed',
  payment_not_refunded: 'Skipped - the payment is not refunded',
  closed_out: 'Applied - the transaction is closed out on your attestation; no payment was touched',
  closeout_voided: 'Applied - the close-out was reversed; the transaction is open and the finding shows again',
  no_active_closeout: 'Skipped - there is no active close-out to reverse',
  not_closed: 'Skipped - the transaction is not closed',
  outcome_date_before_creation: 'Skipped - the outcome date is before the transaction existed',
  already_closed_out: 'Skipped - this transaction is already closed out',
  payment_recorded: 'Skipped - a payment is recorded for this transaction; use Confirm refund',
  refund_date_before_cancel: 'Skipped - the refund date is before the refund path / the Escrow.com cancel',
  replacement_exists: 'Skipped - a replacement balance exists, so this close-out can no longer be reversed',
  no_refund_path: 'Skipped - the refund path has not started',
  no_deadline: 'Skipped - the transaction has no deadline',
  already_secured: 'Skipped - the buyer has already funded it',
  payment_in_flight: 'Skipped - a payment is in flight; the partial-wire finding covers it',
  adverse: 'Skipped - the transaction is flagged, disputed or on the refund path',
  cancel_queued: 'Skipped - a cancel is already queued for this transaction',
  extension_cap: 'Skipped - the maximum number of extensions (3) is used',
  not_later: 'Skipped - the new deadline must be later than the current one',
  too_soon: 'Skipped - the new deadline must be at least 1 minute from now',
  too_far: 'Skipped - the new deadline is beyond the allowed horizon from the original deadline',
  funded_or_in_flight: 'Skipped - funded or in flight; a cancel could lose money',
  // SIL-1840: two-person refund override
  override_requested: 'Applied - override requested; a DIFFERENT ops admin must approve it within 24 hours',
  override_approved: 'Applied - approved; the processor now checks Escrow.com, then you can finalize',
  override_cancelled: 'Applied - the override request was cancelled',
  request_open: 'Skipped - an override request is already open for this transaction',
  evidence_reused: 'Skipped - that statement was already used on another override request',
  request_expired: 'Skipped - the override request expired (24 hours); request it again',
  request_cancelled: 'Skipped - the override request was cancelled',
  request_confirmed: 'Skipped - the override request is already confirmed',
  request_not_pending: 'Skipped - the override request is not waiting for approval',
  self_approval: 'Skipped - a different ops admin than the requester must approve',
  evidence_mismatch: 'Skipped - your statement does not match the requested one (different file)',
  not_a_party: 'Skipped - only the requester or the approver can cancel',
  not_approved: 'Skipped - the override request is not approved yet',
  not_the_approver: 'Skipped - only the approving admin can finalize',
  awaiting_attestation: 'Skipped - waiting for the processor to check Escrow.com; try again in a few minutes',
  attestation_not_bound: 'Skipped - the Escrow.com check does not match this request; cancel and request again',
  attestation_before_approval: 'Skipped - the Escrow.com check predates the approval; cancel and request again',
  attestation_stale: 'Skipped - the Escrow.com check is older than 1 hour; cancel and request again',
  // SIL-1920: approver allowlist + evidence object pinning
  not_an_approver: 'Skipped - you are not on the refund approver list (added by a reviewed database change only)',
  requester_not_allowlisted: 'Skipped - the requester is no longer on the refund approver list; cancel and request again',
  approver_not_allowlisted: 'Skipped - the approving admin is no longer on the refund approver list; cancel and request again',
  evidence_object_mismatch: 'Skipped - the stored statement does not match the requested one; cancel and request again',
  // SIL-1726: two-person refresh of the party emails of a failed create
  refresh_requested: 'Applied - refresh requested; a DIFFERENT ops admin must approve it within 24 hours (nothing has changed yet)',
  emails_refreshed: 'Applied - party emails updated from the org contacts and the create re-queued; both orgs were notified',
  refresh_cancelled: 'Applied - the email refresh request was cancelled',
  refresh_open: 'Skipped - an email refresh request is already open for this transaction',
  refresh_expired: 'Skipped - the email refresh request expired (24 hours); request it again',
  refresh_was_cancelled: 'Skipped - the email refresh request was cancelled',
  refresh_not_pending: 'Skipped - the email refresh request is already approved',
  not_definitive_rejection: 'Skipped - the last create was not a definitive Escrow.com rejection (HTTP 400 / 422); use Retry create',
  contact_recently_changed: 'Skipped - the org contact email changed less than 24 hours ago; wait for the cooling period',
  contact_changer: 'Skipped - the admin who last changed that contact email cannot request or approve its refresh',
  sibling_parties_differ: 'Skipped - another Escrow.com transaction of this deal already uses a different address for that party',
  emails_unchanged: 'Skipped - the org contact emails equal the stored ones; nothing to refresh (use Retry create)',
  no_valid_new_email: 'Skipped - the org contact email is missing or not a valid address; fix the contact first',
  contact_changed_since_request: 'Skipped - a contact email changed since the request; cancel it and request again',
  no_facts: 'Skipped - the deal facts could not be read',
  not_found: 'Skipped - transaction not found',
};

export const escrowTxnKindSchema = z.enum(['deposit', 'balance']);

/** SIL-1742: the active (or last) refund confirmation of a deposit. Ops-only: never in a client DTO. */
export const escrowOpsRefundConfirmationSchema = z.object({
  id: z.number(),
  vendorReference: z.string(),
  refundAmountUsd: z.number(),
  refundedOn: z.string(),
  actorSub: z.string(),
  /** SIL-1840: confirmed by the two-person override; requesterSub is the admin who requested it (also barred from voiding). */
  override: z.boolean().optional(),
  requesterSub: z.string().nullable().optional(),
  environment: z.enum(['sandbox', 'production']),
  createdAt: z.string(),
  voided: z.object({ actorSub: z.string(), createdAt: z.string() }).nullable(),
  /** Server decision: not voided, inside the window, and not the confirming admin. */
  canVoid: z.boolean(),
});
export type EscrowOpsRefundConfirmation = z.infer<typeof escrowOpsRefundConfirmationSchema>;

/** SIL-1742: the active (or last) close-out attestation. Ops-only: never in a client DTO. */
export const escrowOpsRefundCloseoutSchema = z.object({
  id: z.number(),
  outcome: z.enum(ESCROW_CLOSEOUT_OUTCOMES),
  evidenceReference: z.string(),
  outcomeOn: z.string(),
  actorSub: z.string(),
  environment: z.enum(['sandbox', 'production']),
  createdAt: z.string(),
  voided: z.object({ actorSub: z.string(), createdAt: z.string() }).nullable(),
  /** Server decision: not voided, inside the window, and not the closing admin. */
  canVoid: z.boolean(),
});
export type EscrowOpsRefundCloseout = z.infer<typeof escrowOpsRefundCloseoutSchema>;

/** SIL-1840: the open (or last) refund-override request of a deposit. Ops-only. State is derived from the append-only events. */
export const ESCROW_OVERRIDE_STATES = ['requested', 'approved', 'attested', 'fetch_failed', 'confirmed', 'cancelled', 'expired'] as const;
export const escrowOpsRefundOverrideSchema = z.object({
  requestId: z.string().uuid(),
  state: z.enum(ESCROW_OVERRIDE_STATES),
  requestedBy: z.string(),
  requestedAt: z.string(),
  expiresAt: z.string(),
  approvedBy: z.string().nullable(),
  amountUsd: z.number(),
  failureCode: z.string().nullable(),
  /** Server decisions (the DB re-checks and logs a skip): different admin than the requester / the approver / a party. */
  canApprove: z.boolean(),
  canFinalize: z.boolean(),
  canCancel: z.boolean(),
});
export type EscrowOpsRefundOverride = z.infer<typeof escrowOpsRefundOverrideSchema>;

/** One Escrow.com transaction as ops sees it. Non-PII columns only. */
export const escrowOpsTransactionSchema = z.object({
  id: z.string().uuid(),
  kind: escrowTxnKindSchema,
  environment: z.enum(['sandbox', 'production']),
  providerTransactionId: z.string().nullable(),
  /** Deep link to the transaction on Escrow.com, null until it exists. */
  escrowUrl: z.string().nullable(),
  status: z.enum(['pending_create', 'open', 'closed', 'cancelled']),
  amountUsd: z.number(),
  hasBuyerEmail: z.boolean(),
  hasSellerEmail: z.boolean(),
  adverseFlag: z.boolean(),
  inDispute: z.boolean(),
  /** Every Escrow.com flag ever seen true (history). */
  providerFlags: z.record(z.string(), z.boolean()),
  securedAt: z.string().nullable(),
  deadlineAt: z.string().nullable(),
  lastFetchedAt: z.string().nullable(),
  nextFetchAt: z.string().nullable(),
  fetchFailures: z.number(),
  refetchRequestedAt: z.string().nullable(),
  createAttempts: z.number(),
  nextAttemptAt: z.string().nullable(),
  createLeasedUntil: z.string().nullable(),
  awaitingKyb: z.boolean(),
  /** SIL-1597 reversal state. */
  refundPathStartedAt: z.string().nullable(),
  refundConfirmedAt: z.string().nullable(),
  flagClearedAt: z.string().nullable(),
  partnerCancelRequestedAt: z.string().nullable(),
  partnerCancelResultCode: z.string().nullable(),
  partnerCancelResultAt: z.string().nullable(),
  /** SIL-1600 deadline controls: the early-cancel request and why a deposit expired. */
  cancelNowRequestedAt: z.string().nullable(),
  cancelNowResultCode: z.string().nullable(),
  cancelNowResultAt: z.string().nullable(),
  expiryCause: z.enum(['deadline_lapsed', 'ops_cancel']).nullable(),
  supersededAt: z.string().nullable(),
  /** SIL-1598 balance supersede / request audit. */
  supersedeReason: z.enum(['amendment', 'ops_recreate', 'cancelled', 'refund_path']).nullable(),
  supersededCancelRequestedAt: z.string().nullable(),
  supersededCancelDoneAt: z.string().nullable(),
  requestedVia: z.enum(['seller', 'ops']).nullable(),
  /** SIL-1599 completion state (non-PII). */
  completionFetchRequestedAt: z.string().nullable(),
  completionAppliedAt: z.string().nullable(),
  completionResultCode: z.string().nullable(),
  completionRetryAfter: z.string().nullable(),
  completionFailureCount: z.number(),
  disbursedAt: z.string().nullable(),
  /** SIL-1742 (optional: absent from an API that predates it). */
  refundFinding: z.enum(['refund_unconfirmed', 'refund_unrecorded_payment', 'funded_snapshot_unreadable']).nullable().optional(),
  /** Earliest time a refund may be confirmed (refund path start + minimum wait; for a balance also from the last Escrow.com cancel result); null when unknown. */
  refundConfirmableAt: z.string().nullable().optional(),
  refundConfirmation: escrowOpsRefundConfirmationSchema.nullable().optional(),
  /** The active (or last) close-out attestation of a deposit with no recorded payment. */
  refundCloseout: escrowOpsRefundCloseoutSchema.nullable().optional(),
  /** SIL-1840: the open (or last) two-person refund-override request (optional: absent from an API that predates it). */
  refundOverride: escrowOpsRefundOverrideSchema.nullable().optional(),
  /** SIL-1840: true when this deposit's unreadable (items []) snapshot can be handled by the override (finding + deposit + open). */
  refundOverrideEligible: z.boolean().optional(),
  /** SIL-1920: the caller is on the refund approver allowlist (server decision; the DB re-checks). */
  refundApprover: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EscrowOpsTransaction = z.infer<typeof escrowOpsTransactionSchema>;

export const escrowOpsSnapshotSchema = z.object({
  id: z.number(),
  escrowTransactionId: z.string().uuid(),
  fetchedAt: z.string(),
  recordedAt: z.string(),
  /** Current-state flags derived from the snapshot (never the snapshot JSON itself). */
  flags: z.record(z.string(), z.unknown()),
  bindResult: z.string(),
});
export type EscrowOpsSnapshot = z.infer<typeof escrowOpsSnapshotSchema>;

export const escrowOpsFindingSchema = z.object({
  escrowTransactionId: z.string().uuid(),
  kind: z.string(),
  findingKind: z.string(),
  createAttempts: z.number().nullable(),
  createdAt: z.string(),
});

export const escrowOpsAlertSchema = z.object({
  escrowTransactionId: z.string().uuid(),
  alertKind: z.string(),
  openedAt: z.string(),
  clearedAt: z.string().nullable(),
});

export const escrowOpsActionRowSchema = z.object({
  id: z.number(),
  escrowTransactionId: z.string().uuid(),
  action: escrowOpsActionKindSchema,
  /** Cognito sub of the ops_admin (email resolution is a UI follow-up). */
  actorSub: z.string(),
  reason: z.string(),
  result: z.enum(['queued', 'skipped', 'applied']),
  resultDetail: z.string(),
  /** SIL-1600: the deadline before an extend_deadline / cancel_now (cancel_now leaves it unchanged). */
  previousDeadlineAt: z.string().nullable(),
  newDeadlineAt: z.string().nullable(),
  createdAt: z.string(),
});
export type EscrowOpsActionRow = z.infer<typeof escrowOpsActionRowSchema>;

/** SIL-1596: one stored webhook (v_escrow_webhook_events: parsed columns only, never the raw body). */
export const escrowOpsWebhookEventSchema = z.object({
  id: z.number(),
  receivedAt: z.string().nullable(),
  escrowTransactionId: z.string().uuid().nullable(),
  providerTransactionId: z.string().nullable(),
  /** Unverified hint from the payload; our own fetched state is the truth. */
  eventType: z.string().nullable(),
  processedAt: z.string().nullable(),
  processAttempts: z.number(),
  processError: z.string().nullable(),
});
export type EscrowOpsWebhookEvent = z.infer<typeof escrowOpsWebhookEventSchema>;

export const escrowOpsOfferViewSchema = z.object({
  offerId: z.string().uuid(),
  transactions: z.array(escrowOpsTransactionSchema),
  snapshots: z.array(escrowOpsSnapshotSchema),
  findings: z.array(escrowOpsFindingSchema),
  alerts: z.array(escrowOpsAlertSchema),
  actions: z.array(escrowOpsActionRowSchema),
  /** SIL-1742: DB-owned refund timings so the portal never hard-codes them (seconds). */
  refundPolicy: z
    .object({ snapshotMaxAgeSeconds: z.number(), minWaitSeconds: z.number(), voidWindowSeconds: z.number() })
    .optional(),
  /** available=false only from an API that predates the inbox (rollback-compat). */
  webhookEvents: z.object({ available: z.boolean(), items: z.array(escrowOpsWebhookEventSchema) }),
});
export type EscrowOpsOfferView = z.infer<typeof escrowOpsOfferViewSchema>;

/** Row of the transaction list (entry point to the per-offer view). */
export const escrowOpsListRowSchema = z.object({
  id: z.string().uuid(),
  offerId: z.string().uuid(),
  kind: escrowTxnKindSchema,
  status: z.enum(['pending_create', 'open', 'closed', 'cancelled']),
  environment: z.enum(['sandbox', 'production']),
  providerTransactionId: z.string().nullable(),
  amountUsd: z.number(),
  createAttempts: z.number(),
  deadlineAt: z.string().nullable(),
  lastFetchedAt: z.string().nullable(),
  findingCount: z.number(),
  createdAt: z.string(),
});
export type EscrowOpsListRow = z.infer<typeof escrowOpsListRowSchema>;
